import React, { useState, useEffect } from 'react';
import { useLang } from '../contexts/LanguageContext';
import { Globe, Menu, X, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const ORDER_URL = 'https://www.host-on.de/zimbra#pakete';

export default function Header() {
  const { lang, toggle, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navItems = [
    { label: t('nav.features'), href: '#features' },
    { label: t('nav.pricing'), href: '#pricing' },
    { label: t('nav.comparison'), href: '#comparison' },
    { label: t('nav.faq'), href: '#faq' },
    { label: t('nav.contact'), href: '#enterprise' },
  ];

  const scrollTo = (href) => {
    setMobileOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 300);
  };

  return (
    <header
      data-testid="header-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-2xl bg-black/80 border-b border-white/[0.06] shadow-[0_4px_30px_rgba(0,0,0,0.5)]'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <a href="/" className="flex items-center gap-2 group" data-testid="header-logo">
          <div className="w-8 h-8 rounded-lg bg-neon flex items-center justify-center">
            <Zap className="w-4 h-4 text-black" />
          </div>
          <span className="font-heading text-lg lg:text-xl font-black tracking-tighter text-white">
            ZIMBRA<span className="text-neon">.</span>JETZT
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1">
          {navItems.map(item => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="text-sm font-medium text-zinc-500 hover:text-white transition-colors duration-300 tracking-wide uppercase px-4 py-2 rounded-full hover:bg-white/[0.04]"
              data-testid={`nav-${item.href.slice(1)}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggle}
            className="flex items-center gap-1.5 text-sm font-semibold text-zinc-500 hover:text-neon transition-colors px-3 py-2 rounded-full border border-white/[0.06] hover:border-neon/30 bg-white/[0.02]"
            data-testid="lang-toggle"
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === 'de' ? 'EN' : 'DE'}
          </button>

          <a
            href={ORDER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden lg:inline-flex items-center gap-1.5 bg-neon text-black font-bold text-xs uppercase tracking-wider rounded-full px-5 py-2.5 hover:bg-white hover:shadow-[0_0_20px_rgba(212,255,0,0.5)] transition-all duration-300"
            data-testid="cta-header-order"
          >
            {t('nav.order')}
          </a>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2 rounded-lg hover:bg-white/5"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black/95 backdrop-blur-2xl border-t border-white/[0.06] overflow-hidden"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-2">
              {navItems.map(item => (
                <button
                  key={item.href}
                  onClick={() => scrollTo(item.href)}
                  className="text-left text-base font-medium text-zinc-400 hover:text-neon transition-colors py-2 px-3 rounded-xl hover:bg-white/[0.03]"
                  data-testid={`mobile-nav-${item.href.slice(1)}`}
                >
                  {item.label}
                </button>
              ))}
              <a
                href={ORDER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex justify-center bg-neon text-black font-bold text-sm uppercase tracking-wide rounded-full px-6 py-3.5 hover:bg-white transition-all"
                data-testid="cta-mobile-order"
              >
                {t('nav.order')}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
